import React, { useEffect, useCallback, useState } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import {
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  ListGroup,
  Modal,
  Row,
  Spinner
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencil, faTrashAlt } from '@fortawesome/free-solid-svg-icons';

import { subscribeToTab } from 'src/Services/websocket'
import Notification, { useNotification } from 'src/Components/Notification';
import { formatCurrency } from 'src/Utils/formatters';
import { getFromStore, removeFromStore, saveToStore } from 'src/Utils/storage-helpers';
import { updateRaverTab, updateRaverOrder, deleteRaverOrder, getRaverTab } from 'src/Services/api';
import { IS_CLOSED, TabMapper } from './constants';

const TABLE_TAG_STORE_KEY = 'table_tag';

const TabPage = () => {
  const [tab, setTab] = useState({});
  const [tableTag, setTableTag] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [editQuantity, setEditQuantity] = useState(1);
  const [showDelModal, setShowDelModal] = useState(false);
  const [showCloseTabModal, setShowCloseTabModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { notifySuccess, notifyError, onClose, notification } = useNotification();
  
  const { spot, amount, amount_without_charges, charges, slug, currency } = tab;
  let spotId = location.state?.spotId || spot?.id;
  const taxRate = parseFloat(spot?.tax_rate) || 0;
  const totalAmount = parseFloat(amount);

  const onUpdatesReceived = useCallback(tabData => {
    if (tabData) {
      setTab(currentTab => ({ ...currentTab, ...tabData }));
    }
  }, []);

  useEffect(() => {
    if (tab.uuid) {
      return subscribeToTab(tab.uuid, onUpdatesReceived)
    }
  }, [tab.uuid, onUpdatesReceived]);

  const getTabFromStorage = useCallback(async () => {
    const tab = await getFromStore('tab');
    if (tab) setTab(tab);
  }, []);

  useEffect(() => {
    const fetchTabFromApi = async () => {
      if (slug) {
        try {
          const response = await getRaverTab({ slug });
          if (response.status === 200) {
            setTab(response.data);
            await saveToStore('tab', response.data);
          } else {
            await getTabFromStorage();
          }
        } catch (error) {
          await getTabFromStorage();
        }
      } else {
        await getTabFromStorage();
      }
    };

    fetchTabFromApi();
  }, [getTabFromStorage, slug]);

  useEffect(() => {
    const urlTag = searchParams.get("table");
    if (urlTag) {
      setTableTag(urlTag);
      saveToStore(TABLE_TAG_STORE_KEY, urlTag);
    } else {
      getFromStore(TABLE_TAG_STORE_KEY).then((stored) => {
        if (stored) setTableTag(stored);
      });
    }
  }, [searchParams]);

  const handleBack = () => {
    navigate(-1);
  };

  const handleAddFromMenu = () => {
    const menuPath = `/raver/${spotId}/menu`;
    navigate(tableTag ? `${menuPath}?table=${encodeURIComponent(tableTag)}` : menuPath);
  };

  const handleCloseTab = async () => {
    setIsLoading(true);
    const tabStateName = TabMapper[IS_CLOSED];
    const updateResp = await updateRaverTab(
      {slug: tab?.slug}, { action: tabStateName }
    );

    if (updateResp.status !== 200) {
      notifyError({ message: "An error occured while trying to update the tab." });
    } else {
      await removeFromStore('tab', true);
      await removeFromStore(TABLE_TAG_STORE_KEY, true);
      notifySuccess({ message: 'Tab closed successfully!' });
      handleBack()
    }
    setIsLoading(false);
  };

  const handleSave = async () => {
    setIsLoading(true);
    const updatedOrderRes = await updateRaverOrder(
      { id: editingOrder.id },
      { quantity: parseInt(editQuantity, 10) }
    );
    if (updatedOrderRes.status !== 200) {
      notifyError({ message: updatedOrderRes.response.data.detail });
    } else {
      const { data: { tab } } = updatedOrderRes;
      await saveToStore('tab', tab);
      notifySuccess({ message: 'Order updated successfully!' });
    }
    setShowEditModal(false);
    await getTabFromStorage();
    setIsLoading(false);
  };

  const openEditModal = (order) => {
    setEditingOrder(order);
    setEditQuantity(order.quantity);
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setEditingOrder(null);
    setEditQuantity(1);
  };

  const openDelModal = (order) => {
    setEditingOrder(order);
    setShowDelModal(true);
  };
  
  const closeDelModal = () => {
    setShowDelModal(false);
    setEditingOrder(null);
  };

  const handleDelete = async () => {
    setIsLoading(true);
    const delOrderRes = await deleteRaverOrder({ id: editingOrder.id });

    if (delOrderRes.status !== 200) {
      notifyError({ message: delOrderRes.response.data.detail });
    } else {
      const { data } = delOrderRes;
      await saveToStore('tab', data);
      notifySuccess({ message: 'Order deleted successfully!' });
      closeDelModal();
      await getTabFromStorage();
    }
    setIsLoading(false);
  }

  return (
    <Container className="my-4">
      <Notification id="tab-update" onClose={onClose} {...notification} />
      <Row className="justify-content-center">
        <Col md={8} lg={6}>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <Button variant="outline-secondary" onClick={handleBack}>
              ← Back
            </Button>
            <Button
              style={{ backgroundColor: '#41889A', border: 'none' }}
              onClick={handleAddFromMenu}
            >
              + Add from menu
            </Button>
          </div>

          {!tab.orders?.length ? (
            <div className="text-center my-5">
              <p className="text-muted">No items in your tab.</p>
            </div>
          ) : (
            <>
              <Card className="shadow-sm mb-3">
                <Card.Body className="text-center">
                  <h4 className="fw-bold mb-0">{spot?.name}</h4>
                  <small className="text-muted d-block mt-1">Tab Summary</small>
                  <div className="d-flex flex-wrap justify-content-center gap-2 mt-3">
                    {tableTag && (
                      <Badge bg="light" text="dark" className="border">
                        {tableTag}
                      </Badge>
                    )}
                    {tab.email && (
                      <Badge
                        bg="secondary"
                        className="text-truncate"
                        style={{ maxWidth: '100%' }}
                      >
                        {tab.email}
                      </Badge>
                    )}
                  </div>
                </Card.Body>

                <ListGroup variant="flush">
                  {[...tab.orders].sort((a, b) => {
                    if (a.deleted_at && !b.deleted_at) return 1;
                    if (!a.deleted_at && b.deleted_at) return -1;
                    return new Date(b.created_at) - new Date(a.created_at); // or `b.id - a.id`
                  }).map((order) => (
                    <ListGroup.Item
                      key={order?.id}
                      className="d-flex align-items-start py-3"
                      style={{
                        opacity: order.deleted_at ? 0.5 : 1,
                        pointerEvents: order.deleted_at ? 'none' : 'auto', // optional: disables interaction
                        filter: order.deleted_at ? 'blur(1px)' : 'none', // optional: adds blur effect
                      }}
                    >
                      <img
                        src={order?.menu_item.image}
                        alt=''
                        className="rounded me-3 border"
                        style={{
                          width: '50px',
                          height: '50px',
                          objectFit: 'cover',
                        }}
                      />

                      <div className="d-flex flex-column flex-grow-1">
                        <div className="fw-semibold">{order?.menu_item.name}</div>
                        <span
                          className="badge text-light"
                          style={{ backgroundColor: '#41889A', maxWidth: '40px' }}
                        >
                          x{order?.quantity}
                        </span>
                      </div>
                      <div className="d-flex flex-column align-items-end justify-content-between">
                        <div className="fw-semibold mb-2 fw-bold">
                          {formatCurrency(parseFloat(order?.menu_item.price) * order?.quantity, currency)}
                        </div>
                        <div className="d-flex gap-2">
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => openEditModal(order)}
                            disabled={!!order.deleted_at}
                          >
                            <FontAwesomeIcon icon={faPencil} />
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => openDelModal(order)}
                            disabled={!!order.deleted_at}
                          >
                            <FontAwesomeIcon icon={faTrashAlt} />
                          </Button>
                        </div>
                      </div>
                    </ListGroup.Item>
                  ))}
                </ListGroup>

                <Card.Body>
                  <ListGroup variant="flush" className="mb-3">
                    <ListGroup.Item className="d-flex justify-content-between">
                      <span className="text-muted">Amount</span>
                      <span>{formatCurrency(parseFloat(amount_without_charges).toFixed(2), currency)}</span>
                    </ListGroup.Item>
                    {/* <ListGroup.Item className="d-flex justify-content-between">
                      <span className="text-muted">jamlyf Charge</span>
                      <span>₦{jamLyfCharge.toFixed(2)}</span>
                    </ListGroup.Item> */}
                    <ListGroup.Item className="d-flex justify-content-between">
                      <span className="text-muted">Tax ({taxRate}%)</span>
                      <span>{formatCurrency(charges.toFixed(2), currency)}</span>
                    </ListGroup.Item>
                  </ListGroup>

                  <div className="d-flex justify-content-between align-items-center fs-5 fw-bold border-top pt-3">
                    <span>Total</span>
                    <span>{formatCurrency(totalAmount.toFixed(2), currency)}</span>
                  </div>
                </Card.Body>
              </Card>

              <div className="text-center mt-4">
                <Button
                  size="lg"
                  className="w-75 mb-3 py-2"
                  style={{ backgroundColor: '#41889A', border: 'none' }}
                  onClick={() => setShowCloseTabModal(true)}
                >
                  Close tab
                </Button>
              </div>

              <Modal show={showEditModal} onHide={closeEditModal} centered>
                <Modal.Header closeButton>
                  <Modal.Title>Edit Quantity</Modal.Title>
                </Modal.Header>
                {isLoading && (
                  <div className="text-center">
                    <Spinner
                      animation="border"
                      size="md"
                      style={{ color: '#41889A' }}
                    />
                  </div>
                )}
                <Modal.Body>
                  <div className="mb-3">
                    <div className="form-control disable">{editingOrder?.menu_item?.name}</div>
                  </div>
                  <div className="mb-3">
                    <label className="form-label fw-semibold">Quantity</label>
                    <Form.Control
                      type="number"
                      min={1}
                      step={1}
                      value={editQuantity}
                      onChange={(e) => setEditQuantity(e.target.value)}
                    />
                  </div>
                </Modal.Body>
                <Modal.Footer>
                  <Button className='dangerOutlineBtn' onClick={closeEditModal}>
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSave}
                    disabled={editQuantity < 1 || editQuantity === '' || isLoading}
                    className="jamlyfSolidBtn"
                  >
                    Save
                  </Button>
                </Modal.Footer>
              </Modal>

              <Modal show={showDelModal} onHide={closeDelModal} centered>
                <Modal.Header closeButton>
                  <Modal.Title>Confirm Delete</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                  <div className="mb-3">
                    <p>
                      Are you sure you want to delete this order? This action cannot be undone.
                    </p>
                  </div>
                </Modal.Body>
                <Modal.Footer>
                  <Button className='jamlyfOutlineBtn' onClick={closeDelModal}>
                    Cancel
                  </Button>
                  <Button onClick={handleDelete} className='dangerOutlineBtn' disabled={isLoading}>
                    {isLoading && (
                      <Spinner
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        style={{ color: '#fff' }}
                        className='mx-2'
                      />
                    )}
                    Delete
                  </Button>
                </Modal.Footer>
              </Modal>

              <Modal
                show={showCloseTabModal}
                onHide={() => setShowCloseTabModal(false)}
                centered
              >
                <Modal.Body>
                  <div className="mb-3">
                    <p>
                      Are you sure you want to close this Tab? This action cannot be undone.
                    </p>
                  </div>
                </Modal.Body>
                <Modal.Footer>
                  <Button
                    className='jamlyfOutlineBtn'
                    onClick={() => setShowCloseTabModal(false)}
                    disabled={isLoading}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleCloseTab}
                    className='dangerOutlineBtn'
                    disabled={isLoading}
                  >
                    {isLoading && (
                      <Spinner
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        style={{ color: '#fff' }}
                        className='mx-2'
                      />
                    )}
                    Close
                  </Button>
                </Modal.Footer>
              </Modal>
            </>
          )}
        </Col>
      </Row>
    </Container>
  );
};

export default TabPage;
